import { PropsWithChildren, createContext, FC, useContext, useEffect, useState } from 'react';
import { LDContextCommon, LDFlagSet, LDMultiKindContext } from 'launchdarkly-js-sdk-common';
import { initialize as initializeLDClient, LDClient } from 'launchdarkly-js-client-sdk';

import { getLocale } from '../../util/locales/locales';
import { useCore } from '../../feature/context/core.context';

const SharedFeatureFlagsContext = createContext<LDFlagSet>({});

let ldClient: LDClient | undefined;
const clientId = process.env.NEXT_PUBLIC_LAUNCHDARKLY_SHARED_CLIENT_SIDE_ID;

if (typeof window !== 'undefined' && clientId) {
  ldClient = initializeLDClient(clientId, {
    kind: 'multi',
    user: {
      key: 'anonymous',
    },
  });
}

interface SharedFeatureFlagsContextProps {
  requiresAuthentication?: boolean;
}

export const SharedFeatureFlagsContextProvider: FC<PropsWithChildren<SharedFeatureFlagsContextProps>> = ({
  children,
  requiresAuthentication,
}) => {
  const core = useCore();
  const {
    auth: { user },
  } = core;

  const getUserContext = (): LDContextCommon => {
    if (typeof requiresAuthentication !== 'boolean') {
      return {};
    }
    return requiresAuthentication
      ? { key: user?.anonymousId, country: user?.country }
      : { key: 'anonymous', country: getLocale().country?.toUpperCase() };
  };
  const { key: userKey, country } = getUserContext();
  const ldContextUserCountry = ((ldClient?.getContext() as LDMultiKindContext)?.user as LDContextCommon)?.country;
  const [flags, setFlags] = useState<LDFlagSet>({});

  useEffect(() => {
    if (ldClient && userKey && country && country !== ldContextUserCountry) {
      ldClient
        .identify({
          kind: 'multi',
          user: {
            key: userKey,
            country,
            locale: user?.locale || getLocale().toString(),
          },
        })
        .then(setFlags);
    }
  }, [userKey, country, ldContextUserCountry]);

  return <SharedFeatureFlagsContext.Provider value={flags}>{children}</SharedFeatureFlagsContext.Provider>;
};

export const useSharedFeatureFlags = () => {
  const context = useContext(SharedFeatureFlagsContext);

  if (!context) {
    throw new Error(`useSharedFlags must be used within a SharedFlagsContextProvider`);
  }

  return context;
};
